import { button, element, heading, table } from "@groundspark/browser-ui";
import { breadcrumbs } from "./breadcrumbs";
import type { BranchApi } from "./branch-api";
import { channelLabels, type Communication, type CommunicationDelivery } from "./domain";
import { emailDisplayText } from "./email-content";

const times = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" });

export class CommunicationHistory {
  public readonly element: HTMLElement;
  private readonly statuses = element("div");
  private request: AbortController | null = null;
  private cursor: string | null = null;
  private recipients: readonly CommunicationDelivery[] = [];

  public constructor(
    private readonly item: Communication,
    private readonly branchId: string,
    private readonly api: BranchApi,
    onCommunications: () => void | Promise<void>,
    private readonly onFailure: (error: unknown) => boolean,
  ) {
    if (item.submittedAt === null) throw new TypeError("Expected a submitted communication");
    this.element = element(
      "div",
      "communication-history",
      breadcrumbs(`Sent ${channelLabels[item.channel].toLowerCase()}`, [
        { label: "Communications", onSelect: onCommunications },
      ]),
      heading(2, item.title?.trim() || "Sent message", "panel-heading__title"),
      element(
        "p",
        "muted",
        `${channelLabels[item.channel]} · Sent ${times.format(item.submittedAt)} · ${item.recipientCount} ${item.recipientCount === 1 ? "recipient" : "recipients"}`,
      ),
      element(
        "section",
        "message-preview",
        heading(3, "Message"),
        element(
          "p",
          "message-preview__body",
          item.channel === "email" ? emailDisplayText(item.body) : item.body,
        ),
      ),
      heading(3, "Delivery details"),
      this.statuses,
    );
  }

  public destroy(): void {
    this.request?.abort();
    this.request = null;
    this.element.replaceChildren();
    this.recipients = [];
  }

  private render(): void {
    const appNotification = this.item.channel === "app_notification";
    this.statuses.replaceChildren(
      element(
        "p",
        "muted",
        appNotification
          ? "Inbox availability and reading are separate from push alerts. Push acceptance does not confirm delivery to a device."
          : this.item.channel === "email"
            ? "Accepted means the email provider has queued the message. Delivered means the recipient’s mail server accepted it; it does not confirm that it was read."
            : "Delivery status for each recipient.",
      ),
      table(
        [
          "Member",
          appNotification ? "Inbox" : "Delivery",
          ...(appNotification ? ["Push alerts"] : []),
        ],
        this.recipients.map((recipient) => ({
          cells: [
            recipient.displayName,
            recipient.readAt !== null
              ? `Read ${times.format(recipient.readAt)}`
              : appNotification && recipient.state === "delivered"
                ? "In app inbox"
                : recipient.state,
            ...(appNotification
              ? [
                  [
                    recipient.pushQueued ? `${recipient.pushQueued} queued` : "",
                    recipient.pushAccepted ? `${recipient.pushAccepted} accepted` : "",
                    recipient.pushFailed ? `${recipient.pushFailed} failed` : "",
                    recipient.pushCancelled ? `${recipient.pushCancelled} cancelled` : "",
                  ]
                    .filter(Boolean)
                    .join(", ") || "No registered device",
                ]
              : []),
          ],
        })),
      ),
    );
    this.statuses.append(
      button("Refresh delivery status", "button button--white", () => this.load()),
    );
    if (this.cursor !== null)
      this.statuses.append(
        button("More recipients", "button button--white", () => this.load(false)),
      );
  }

  public async load(reset = true): Promise<void> {
    this.request?.abort();
    const active = new AbortController();
    this.request = active;
    const progress = element("p", null, "Loading delivery statuses…");
    progress.setAttribute("role", "status");
    this.statuses.replaceChildren(progress);
    try {
      const page = await this.api.deliveries(
        this.branchId,
        this.item.id,
        reset ? null : this.cursor,
        active.signal,
      );
      if (this.request !== active || !this.element.isConnected) return;
      this.recipients = reset ? page.items : [...this.recipients, ...page.items];
      this.cursor = page.nextCursor;
      this.render();
    } catch (error) {
      if (this.request !== active || active.signal.aborted || !this.element.isConnected) return;
      if (!this.onFailure(error)) {
        const alert = element(
          "p",
          "composer__error",
          error instanceof Error ? error.message : String(error),
        );
        alert.setAttribute("role", "alert");
        this.statuses.replaceChildren(
          alert,
          button("Retry", "button button--white", () => this.load(reset)),
        );
      }
    } finally {
      if (this.request === active) this.request = null;
    }
  }
}
